<template>
  <div class="blocks-gallery-full-screen" :class="{'--open': isGalleryOpen}">
    <div class="__gallery-tool-bar">
      <h3 class="__gallery-image-caption">
        {{ images && images[selectedSlide - 1] && images[selectedSlide - 1].caption ? images[selectedSlide - 1].caption : '' }}
      </h3>
      <ElementsButtonsIcon class="blocks-gallery-full-screen__close-btn" :icon-src="closeIcon" icon-alt="Close button icon" @click="closeGallery()" />
    </div>

    <!-- Gallery slider -->
    <BlocksSlider v-model="selectedSlide" @swipeDown="closeGallery()">
      <!-- Gallery item -->
      <div v-for="(image, key) in images" :key="`gallery-items-${key}`" class="__gallery-item">
        <div v-if="image?.type === 'video'" class="__video-wrapper">
          <client-only>
            <LazyBlocksYoutube ref="videoBlock" :video-url="image.url" :auto-play="false" hide-play-button />
          </client-only>
        </div>
        <div v-else class="__image-wrapper">
          <img v-lazy-load  :data-src="image.url" alt="gallery image">
        </div>
      </div>
    </BlocksSlider>

    <div v-if="images.length" class="__gallery-image-count">
      {{ selectedSlide }} / {{ images.length }}
    </div>
  </div>
</template>

<script setup lang="ts">
import closeIcon from '~/assets/images/icons/close.webp';
import {Ref} from 'vue';
import {ImageItem} from '~/@types/common';
import {AnalyticsDataProps} from '~/@types/analytics';

// DATA Definitions
//----------------------------------------------------------------------------------------------------------------------
const isGalleryOpen: Ref<boolean> = ref(false);
const selectedSlide: Ref<number> = ref(0);
const videoBlock: Ref<HTMLElement | null> = ref(null);

// PROPS
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  images: {
    type: Array as PropType<ImageItem[]>,
    default: () => [],
  },
  selectedImageIndex: {
    type: Number,
    default: 0,
  },
  analyticsEventData: {
    type: [Object, null] as PropType<AnalyticsDataProps>,
    default: () => null,
  },
});

// EMIT definitions
const emit = defineEmits<{
  (event: 'update:modelValue', payload: boolean): void;
}>();

/**
 * Component MOUNTED!
 */
onMounted(() => {
  isGalleryOpen.value = props.modelValue;
  emit('update:modelValue', isGalleryOpen.value);

  if (props.selectedImageIndex) {
    selectedSlide.value = props.selectedImageIndex;
  }
  setTimeout(() => {
    initVideos();
  }, 2000);
});

/**
 * @_WATCHERS - Gallery open/close
 */
watch(() => props.modelValue, () => {
  isGalleryOpen.value = !!props.modelValue;

  if (props.selectedImageIndex) {
    selectedSlide.value = props.selectedImageIndex;
  }
});

watch(() => isGalleryOpen.value, () => {
  if (isGalleryOpen.value) {
    document.body.classList.add('no-scroll');
  } else {
    document.body.classList.remove('no-scroll');
  }
});

function initVideos () {
  if (videoBlock.value && (videoBlock.value as any).length) {
    (videoBlock.value as any).forEach((video: any) => {
      if (video && video?.initIframe) {
        video?.initIframe();
      }
    });
  }
}

/**
 * Close gallery
 */
function closeGallery () {
  isGalleryOpen.value = false;
  emit('update:modelValue', isGalleryOpen.value);
  if (videoBlock.value && (videoBlock.value as any).length) {
    (videoBlock.value as any).forEach((video: any) => {
      if (video && video?.pauseVideo) {
        video?.pauseVideo();
      }
    });
  }
}
</script>

<style lang="scss">
.blocks-gallery-full-screen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  width: 0;
  height: 0;
  overflow: hidden;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease-in-out,
  width 0.4s 0.1s ease-in-out,
  height 0.4s 0.1s ease-in-out;

  &.--open {
    width: 100%;
    height: 100%;
    opacity: 1;
    transition: opacity 0.3s 0.1s ease-in-out,
    width 0.3s ease-in-out,
    height 0.3s ease-in-out;
  }

  .__gallery-tool-bar {
    position: absolute;
    backdrop-filter: blur(8px);
    top: 0;
    left: 0;
    width: 100%;
    height: 40px;
    background-color: rgba(0, 0, 0, 0.7);
    z-index: 100;
    color: rgb(255, 255, 255);
    display: flex;
    align-items: center;
    padding: 0 20px;
    justify-content: space-between;

    .__gallery-image-caption {
      font-weight: 400;
      font-size: 14px;
      text-align: center;
      width: 100%;
    }

    .el-icon-button {
      &:hover {
        background-color: transparent;
      }
    }
  }

  .blocks-slider {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100vw;
    height: 100vh;

    .__gallery-item {
      max-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;

      .__image-wrapper {
        img {
          max-width: 100%;
          max-height: 100%;
        }
      }

      .__video-wrapper {
        width: 100%;
        height: 100vh;
      }
    }

    .__slider-nav {
      &.__slider-go-back {
        left: 0 !important;
      }
      &.__slider-go-forward {
        right: 0 !important;
      }
    }
  }

  .__gallery-image-count {
    position: absolute;
    bottom: 12px;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(4px);
    padding: 6px 16px;
    border-radius: 16px;
    color: #fff;
  }
}
</style>
